import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { PuffLoader } from "react-spinners";
import Swal from "sweetalert2";

interface LeadAssets {
  images_url: string[];
  lat: string | null;
  lon: string | null;
  description: string;
  date: string;
  phone_number: string;
  status: string;
  lead_id: string;
  asset_type?: string;
  inviter: {
    uname: string;
    profile_image: string;
    role: string;
    phone_number: string;
    age: string;
    uid: string;
  };
  googlemap_url: string;
}

const STATUS_COLOR: Record<string, string> = {
  pending: "#f59e0b",
  waiting: "#3b82f6",
  success: "#10b981",
  failed:  "#f43f5e",
};

const ASSET_TYPE_LABEL: Record<string, string> = {
  house: "บ้าน",
  condo: "คอนโด",
  land: "ที่ดิน",
  commercial: "อาคารพาณิชย์",
  townhouse: "ทาวน์เฮาส์",
};

function getStatusBadgeColor(status: string): string {
  return STATUS_COLOR[status] ?? "#9ca3af";
}

function getActivePillStyle(label: string): React.CSSProperties {
  const color = STATUS_COLOR[label] ?? "#6b7280";
  return { backgroundColor: color, borderColor: color, color: "#fff" };
}

const TABS = [
  { label: "All", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Waiting", value: "waiting" },
  { label: "Success", value: "success" },
  { label: "Failed", value: "failed" },
];

const STATUS_BUTTONS = ["pending", "waiting", "success", "failed"];

function Lead(): JSX.Element {
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string>("all");
  const [filteredData, setFilteredData] = useState<LeadAssets[]>([]);
  const [searchLeadValue, setSearchLeadValue] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [LimitEstate, setLimitEstate] = useState("100");

  // ── Filter states ──
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
  const [assetType, setAssetType] = useState<string>("all");

  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getReportLead(selectedTab, pageNumber, sortOrder, assetType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getReportLead(selectedTab, pageNumber, sortOrder, assetType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber]);

  const getReportLead = (
    status: string = selectedTab,
    page: number = pageNumber,
    sort: "ASC" | "DESC" = sortOrder,
    type: string = assetType,
    limit: string = LimitEstate
  ) => {
    setLoading(true);
    const statusParam = status && status !== "all" ? `&status=${status}` : "";
    const typeParam   = type && type !== "all" ? `&type=${type}` : "";
    const sortParam   = `&sort=${sort}`;

    axios
      .get(
        `${process.env.REACT_APP_ADMIN_API}/reports/lead?limit=${limit}&page=${page}${sortParam}${statusParam}${typeParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res: any) => {
        setFilteredData(res.data.data);
        setTotalPages(res.data.totalPages ?? 1);
        setLoading(false);
      })
      .catch(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("permission");
        setLoading(false);
        window.location.replace("/auth/login");
      });
  };

  const handleSortedByStatus = (status: string) => {
    setSelectedTab(status);
    setPageNumber(1);
    getReportLead(status, 1, sortOrder, assetType);
  };

  const handleResetFilters = () => {
    setSortOrder("DESC");
    setAssetType("all");
    setPageNumber(1);
    getReportLead(selectedTab, 1, "DESC", "all");
  };

  const handleToggleSort = () => {
    const next: "ASC" | "DESC" = sortOrder === "DESC" ? "ASC" : "DESC";
    setSortOrder(next);
    setPageNumber(1);
    getReportLead(selectedTab, 1, next, assetType);
  };

  const handleAssetTypeChange = (type: string) => {
    setAssetType(type);
    setPageNumber(1);
    getReportLead(selectedTab, 1, sortOrder, type);
  };

  const handleLimitChange = (limit: string) => {
    setLimitEstate(limit);
    setPageNumber(1);
    getReportLead(selectedTab, 1, sortOrder, assetType, limit);
  };

  const handleChangeStatusLead = (status: string, lead_id: string, statusLabel: string) => {
    if (status === "waiting" && statusLabel === "waiting") {
      sessionStorage.setItem("lead_id_posts", lead_id);
      window.location.replace("/create/select/post");
      return;
    }
    axios
      .post(
        `${process.env.REACT_APP_ADMIN_API}/reports/lead/update`,
        { report_state: statusLabel, lead_id },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        Swal.fire(res.data.message, "", "success");
        getReportLead(selectedTab, pageNumber, sortOrder, assetType);
      })
      .catch((err) => {
        Swal.fire(err?.response?.data?.Error ?? "เกิดข้อผิดพลาด", "", "error");
      });
  };

  const handleNote = (id: string) => {
    sessionStorage.setItem("idasset_note", id);
    window.open("/lead/note", "_blank");
  };

  const handleNavigateToMap = (lat: string | null, lng: string | null, mapsUrl: string) => {
    if (lat && lng) {
      window.open(mapsUrl, "_blank");
    } else {
      Swal.fire("ไม่มีตำแหน่งในฐานข้อมูล", "", "error");
    }
  };

  const handleDeleteLead = (id: string) => {
    Swal.fire({
      title: "คุณแน่ใจหรือไม่?",
      text: `ลบ LEAD ${id} การกระทำนี้ไม่สามารถย้อนกลับได้`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ลบเลย",
      cancelButtonText: "ยกเลิก",
    }).then((result) => {
      if (!result.isConfirmed) return;
      axios
        .delete(`${process.env.REACT_APP_ADMIN_API}/reports/lead/delete/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(() => {
          Swal.fire("ลบสำเร็จ", "", "success");
          getReportLead(selectedTab, pageNumber, sortOrder, assetType);
        })
        .catch(() => {
          Swal.fire("ไม่สามารถลบ LEAD ได้", "", "error");
        });
    });
  };

  const enterSearchLead = () => {
    if (!searchLeadValue.trim()) {
      getReportLead(selectedTab, pageNumber, sortOrder, assetType);
      return;
    }
    setLoading(true);
    setFilteredData([]);
    axios
      .get(
        `${process.env.REACT_APP_ADMIN_API}/reports/lead/search?sort=ASC&leadID=${searchLeadValue}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res: any) => {
        setFilteredData(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const hasActiveFilters = sortOrder !== "DESC" || assetType !== "all";

  return (
    <div className="w-full h-screen overflow-y-auto bg-gray-50" ref={topRef}>
      {/* ── Sticky Header ── */}
      <div className="sticky top-0 z-10 w-full bg-white border-b border-gray-100">
        <div className="w-full px-6 py-3 flex flex-col gap-2.5">
          {/* Row 1: Title + Search */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg font-semibold text-gray-900 tracking-tight">
                Lead Management
              </h1>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">
                {filteredData.length} รายการ
              </span>
            </div>

            {/* Search */}
            <div className="relative flex-shrink-0">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchLeadValue}
                onChange={(e) => setSearchLeadValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && enterSearchLead()}
                placeholder="ค้นหา Lead ID..."
                className="pl-8 pr-4 py-2 text-sm border border-gray-200 rounded-full w-64 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200 transition-all bg-gray-50 focus:bg-white placeholder:text-gray-400"
              />
            </div>
          </div>

          {/* Row 2: Status Tabs */}
          <div className="flex items-center gap-1">
            {TABS.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => handleSortedByStatus(value)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedTab === value
                    ? "bg-gray-900 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Row 3: Filters */}
          <div className="flex items-center gap-2 flex-wrap pb-0.5">
            {/* Sort toggle */}
            <button
              onClick={handleToggleSort}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-lg transition-colors ${
                sortOrder !== "DESC"
                  ? "border-gray-800 bg-gray-900 text-white"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d={sortOrder === "DESC"
                    ? "M3 4h13M3 8h9M3 12h5m10 0l-4 4m4-4l-4-4"
                    : "M3 4h13M3 8h9M3 12h5m10 4l-4-4m4 4l-4-4"}
                />
              </svg>
              {sortOrder === "DESC" ? "ใหม่ → เก่า" : "เก่า → ใหม่"}
            </button>

            {/* Asset type */}
            <select
              value={assetType}
              onChange={(e) => handleAssetTypeChange(e.target.value)}
              className={`text-xs border rounded-lg px-3 py-1.5 outline-none focus:border-gray-400 bg-white transition-colors cursor-pointer ${
                assetType !== "all" ? "border-gray-800 text-gray-900 font-medium" : "border-gray-200 text-gray-600"
              }`}
            >
              <option value="all">ทุกประเภท</option>
              <option value="house">บ้าน</option>
              <option value="condo">คอนโด</option>
              <option value="land">ที่ดิน</option>
              <option value="commercial">อาคารพาณิชย์</option>
              <option value="townhouse">ทาวน์เฮาส์</option>
            </select>

            {/* Limit per page */}
            <select
              value={LimitEstate}
              onChange={(e) => handleLimitChange(e.target.value)}
              className="text-xs border rounded-lg px-3 py-1.5 outline-none focus:border-gray-400 bg-white transition-colors cursor-pointer border-gray-200 text-gray-600"
            >
              {["50", "100", "200", "500", "1000"].map((n) => (
                <option key={n} value={n}>{n} รายการ/หน้า</option>
              ))}
            </select>

            {/* Reset */}
            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-1 px-3 py-1.5 text-xs border border-dashed border-gray-300 rounded-lg text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-colors"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                ล้าง
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="w-full px-6 py-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <PuffLoader color="#374151" />
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <svg
              className="w-14 h-14 mb-3 opacity-25"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-sm">ไม่พบ Lead</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredData.map((lead) => {
              const { lead_id, images_url, phone_number, description, lat, lon, status, googlemap_url, asset_type } = lead;
              const imageUrl = images_url?.[0] ?? "";

              return (
                <motion.div
                  key={lead_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* ── Image ── */}
                  <div className="relative h-48 bg-gray-900">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt="lead"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}

                    {/* Status badge */}
                    <span
                      className="absolute top-3 right-3 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest"
                      style={{ backgroundColor: getStatusBadgeColor(status) }}
                    >
                      {status}
                    </span>

                    {/* Note button */}
                    <button
                      onClick={() => handleNote(lead_id)}
                      className="absolute top-3 left-3 w-7 h-7 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-white transition-colors backdrop-blur-sm"
                      title="จดโน้ต"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>

                    {/* Delete button */}
                    <button
                      onClick={() => handleDeleteLead(lead_id)}
                      className="absolute top-12 left-3 w-7 h-7 rounded-full bg-black/50 hover:bg-rose-600/90 flex items-center justify-center text-white transition-colors backdrop-blur-sm"
                      title="ลบ Lead"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>

                    {/* Map button */}
                    <button
                      onClick={() => handleNavigateToMap(lat, lon, googlemap_url)}
                      className="absolute bottom-3 right-3 w-7 h-7 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-white transition-colors backdrop-blur-sm"
                      title="ดูแผนที่"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </button>

                    {/* Lead ID */}
                    <div className="absolute bottom-3 left-3 text-white/80 text-[9px] font-mono bg-black/50 px-2 py-0.5 rounded-full backdrop-blur-sm max-w-[55%] truncate">
                      {lead_id}
                    </div>
                  </div>

                  {/* ── Body ── */}
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-base font-semibold text-gray-800 tracking-wide">
                        {phone_number}
                      </p>
                      {asset_type && (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">
                          {ASSET_TYPE_LABEL[asset_type] ?? asset_type}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed line-clamp-2">
                      {description?.slice(0, 120) ?? "—"}
                    </p>

                    {/* ── Status buttons ── */}
                    <div className="mt-3 grid grid-cols-2 gap-1.5">
                      {STATUS_BUTTONS.map((label) => (
                        <button
                          key={label}
                          onClick={() => {
                            if (status === label && label !== "waiting") return;
                            handleChangeStatusLead(status, lead_id, label);
                          }}
                          className="text-xs font-medium py-1.5 rounded-lg border transition-all text-gray-400 hover:text-gray-600 hover:border-gray-400"
                          style={status === label ? getActivePillStyle(label) : {}}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="w-full bg-white border-t border-gray-100 py-3">
          <div className="flex items-center justify-center gap-3">
            <button
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber((p) => p - 1)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-200 rounded-full disabled:opacity-30 hover:bg-gray-50 disabled:cursor-not-allowed transition-colors text-gray-600"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              ก่อนหน้า
            </button>
            <span className="text-sm font-medium text-gray-700 min-w-[80px] text-center">
              {pageNumber}{" "}
              <span className="text-gray-300 font-light">/</span>{" "}
              {totalPages}
            </span>
            <button
              disabled={pageNumber >= totalPages}
              onClick={() => setPageNumber((p) => p + 1)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-200 rounded-full disabled:opacity-30 hover:bg-gray-50 disabled:cursor-not-allowed transition-colors text-gray-600"
            >
              ถัดไป
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Scroll to top ── */}
      <button
        onClick={() => topRef.current?.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-16 right-6 w-9 h-9 rounded-full bg-gray-900 text-white flex items-center justify-center shadow-lg hover:bg-gray-700 transition-colors z-50"
        title="กลับด้านบน"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </button>
    </div>
  );
}

export default Lead;
